import { createDomElementHandler } from "@/scripts/utils/dom.utils";
import { debounce } from "@/scripts/utils/debounce.util";
import { AbPc1Element } from "@/scripts/components/ab-pc-1/abPc1.element";
import { ProductCardViewModel } from "../variants/purchaseCta.types";
import { getPaginationItems } from "./paginationItems";
import { getInterstitialInsertAfter } from "./interstitialPosition";

export interface ProductGridRendererOptions {
  className: string;
  cardsPerPage?: number;
}

/** Renders already-resolved card models as a paginated responsive grid. */
export class ProductGridRenderer {
  #models: readonly ProductCardViewModel[] = [];
  #cards = new Map<string, AbPc1Element>();
  #wrapperEl: HTMLElement;
  #cardsEl: HTMLElement;
  #paginationEl: HTMLElement;
  #interstitialTemplate: HTMLTemplateElement | null = null;
  #interstitialAfterRow = 0;
  #cardsPerRow = 1;
  #currentPage = 0;
  #resizeObserver: ResizeObserver;

  readonly #cardsPerPage: number;
  readonly #className: string;

  constructor(
    private readonly host: HTMLElement,
    options: ProductGridRendererOptions,
  ) {
    this.#className = options.className;
    this.#cardsPerPage = options.cardsPerPage ?? 12;
    this.#wrapperEl = createDomElementHandler("div", this.#className);
    this.#cardsEl = createDomElementHandler("div", `${this.#className}__cards`);
    this.#paginationEl = createDomElementHandler("div", `${this.#className}__pagination`);
    this.#wrapperEl.replaceChildren(this.#cardsEl, this.#paginationEl);
    this.host.appendChild(this.#wrapperEl);
    this.#paginationEl.addEventListener("click", this.#onPaginationClick);
    this.#resizeObserver = new ResizeObserver((entries) => {
      const width = entries.find(({ target }) => target === this.host)?.contentRect.width;
      if (width) this.#updateLayoutDebounced(width);
    });
    this.#resizeObserver.observe(this.host);
  }

  setModels(models: readonly ProductCardViewModel[]) {
    this.#models = models;
    const availableIds = new Set(models.map(({ product }) => String(product._id)));
    this.#cards.forEach((card, id) => {
      if (!availableIds.has(id)) this.#cards.delete(id);
    });
    models.forEach((model) => this.#getCard(model).cardModel = model);
    this.#setPagination();
    this.#updateLayout(this.host.offsetWidth);
  }

  setLoading() {
    this.#wrapperEl.classList.add(`${this.#className}--loading`);
  }

  setError() {
    this.#wrapperEl.classList.add(`${this.#className}--error`);
    this.#cardsEl.replaceChildren();
    this.#paginationEl.replaceChildren();
  }

  setInterstitial(template: HTMLTemplateElement | null, afterRow: number | null) {
    if (this.#interstitialTemplate === template && this.#interstitialAfterRow === (afterRow ?? 0)) return;
    this.#interstitialTemplate = template;
    this.#interstitialAfterRow = afterRow ?? 0;
    if (this.#models.length) this.#goToPage(this.#currentPage);
  }

  destroy() {
    this.#paginationEl.removeEventListener("click", this.#onPaginationClick);
    this.#resizeObserver.disconnect();
    this.#wrapperEl.remove();
  }

  #getCard(model: ProductCardViewModel): AbPc1Element {
    const id = String(model.product._id);
    const existing = this.#cards.get(id);
    if (existing) return existing;
    const card = createDomElementHandler("ab-pc-1") as AbPc1Element;
    this.#cards.set(id, card);
    return card;
  }

  #onPaginationClick = (event: Event) => {
    const target = event.target as HTMLElement;
    if (target.closest(`.${this.#className}__pagination-prev`)) {
      this.#goToPage(this.#currentPage - 1, true);
      return;
    }
    if (target.closest(`.${this.#className}__pagination-next`)) {
      this.#goToPage(this.#currentPage + 1, true);
      return;
    }
    const pageTarget = target.closest<HTMLButtonElement>(`.${this.#className}__pagination-page`);
    if (pageTarget) this.#goToPage(Number(pageTarget.dataset.page), true);
  };

  #setPagination() {
    this.#wrapperEl.classList.remove(`${this.#className}--loading`, `${this.#className}--error`);
    this.#paginationEl.replaceChildren();
    this.#goToPage(0);
  }

  #renderPagination() {
    const pagesCount = Math.ceil(this.#models.length / this.#cardsPerPage);
    if (pagesCount <= 1) {
      this.#paginationEl.replaceChildren();
      return;
    }

    const width = this.#paginationEl.clientWidth || this.host.offsetWidth;
    // Page buttons have a minimum width; measure the rendered controls as
    // well, since a long page number or a theme font can make them wider.
    let maxItems = Math.min(pagesCount, Math.max(5, Math.floor(width / 32)));
    const gap = parseFloat(getComputedStyle(this.#paginationEl).columnGap) || 0;
    while (true) {
      const previous = document.createElement("button");
      previous.type = "button";
      previous.className = `${this.#className}__pagination-prev`;
      previous.setAttribute("aria-label", "Pagina precedente");
      previous.textContent = "←";
      previous.disabled = this.#currentPage === 0;

      const items = getPaginationItems(pagesCount, this.#currentPage, maxItems).map((item) => {
        if (item === "ellipsis") {
          const ellipsis = document.createElement("span");
          ellipsis.className = `${this.#className}__pagination-ellipsis`;
          ellipsis.setAttribute("aria-hidden", "true");
          ellipsis.textContent = "…";
          return ellipsis;
        }
        const button = document.createElement("button");
        button.type = "button";
        button.className = `${this.#className}__pagination-page`;
        button.dataset.page = String(item);
        button.setAttribute("aria-label", `Pagina ${item + 1}`);
        button.textContent = String(item + 1);
        if (item === this.#currentPage) button.setAttribute("aria-current", "page");
        return button;
      });

      const next = document.createElement("button");
      next.type = "button";
      next.className = `${this.#className}__pagination-next`;
      next.setAttribute("aria-label", "Pagina successiva");
      next.textContent = "→";
      next.disabled = this.#currentPage === pagesCount - 1;
      this.#paginationEl.replaceChildren(previous, ...items, next);

      const controls = Array.from(this.#paginationEl.children) as HTMLElement[];
      const usedWidth = controls.reduce((sum, control) => sum + control.offsetWidth, 0) + gap * (controls.length - 1);
      if (!width || usedWidth <= width || maxItems === 5) break;
      maxItems--;
    }
  }

  #goToPage(page: number, scroll = false) {
    const pagesCount = Math.ceil(this.#models.length / this.#cardsPerPage);
    if (!pagesCount) page = 0;
    else page = Math.max(0, Math.min(page, pagesCount - 1));
    if (scroll && page !== this.#currentPage) this.host.scrollIntoView({ behavior: "smooth", block: "start" });
    this.#currentPage = page;
    const models = this.#models.slice(page * this.#cardsPerPage, (page + 1) * this.#cardsPerPage);
    const insertAfter = this.#interstitialTemplate && this.#interstitialAfterRow > 0 && this.#interstitialTemplate.innerHTML.trim()
      ? getInterstitialInsertAfter(models.length, this.#cardsPerRow, this.#interstitialAfterRow)
      : null;
    const fragment = document.createDocumentFragment();
    models.forEach((model, index) => {
      fragment.appendChild(this.#getCard(model));
      if (index + 1 === insertAfter && this.#interstitialTemplate) {
        const banner = document.createElement("div");
        banner.className = `${this.#className}__banner`;
        banner.appendChild(this.#interstitialTemplate.content.cloneNode(true));
        fragment.appendChild(banner);
      }
    });
    this.#cardsEl.replaceChildren(fragment);
    this.#renderPagination();
  }

  #updateLayoutDebounced = debounce(this.#updateLayout.bind(this), 200);

  #updateLayout(width: number) {
    if (!this.#models.length) return;
    const gap = 32;
    const horizontalColumns = Math.max(1, Math.floor((width + gap) / (475 + gap)));
    const verticalColumns = Math.max(1, Math.floor((width + gap) / (300 + gap)));
    const horizontal = width >= 475 && !(verticalColumns >= 2 && horizontalColumns < 2);
    const compact = width < 375;
    const cardsPerRow = horizontal ? horizontalColumns : verticalColumns;
    const changed = cardsPerRow !== this.#cardsPerRow;
    this.#cardsPerRow = cardsPerRow;
    this.#wrapperEl.classList.toggle(`${this.#className}--horizontal`, horizontal);
    this.#cards.forEach((card) => {
      card.setAttribute("data-layout", horizontal ? "horizontal" : "vertical");
      card.toggleAttribute("data-view", compact);
      if (compact) card.setAttribute("data-view", "compact");
    });
    if (changed && this.#interstitialTemplate) this.#goToPage(this.#currentPage);
    else this.#renderPagination();
  }
}
